import moment from 'moment';
import { Activity, ActivityItem, GetActivitiesDto } from './UserActivityTypes';

const colors = ADV.settings.color_groups.reduce((acc, cur) => acc.concat(cur), []) as string[];
const pickedColors = [] as { initials: string; color: string }[];

export const draftColor = (initials: string): string => {
  const pick = Math.floor(Math.random() * colors.length);
  const color = colors[pick];

  const drafted = pickedColors.find(draft => draft.initials === initials);

  if (drafted) {
    return drafted.color;
  }

  colors.splice(pick, 1);
  pickedColors.push({
    initials,
    color,
  });

  return color;
};

type RegroupItemsStep1 = Activity[];
type RegroupItemsStep2 = {
  Date: string;
  DayActivity: Record<string, Activity[]>;
}[];
type RegroupItemsStep3 = ActivityItem[];
/**
 * Due to timezone differences, some activities might belong to a different day in local time.
 * This function parses the response and regroups the activities considering local time.
 *
 * (Note: Times in response are UTC)
 */
export const regroupItems = (data: GetActivitiesDto) => {
  const { Items } = data;

  // Step1 ➜ Flatten the response
  const step1: RegroupItemsStep1 = Items.reduce((itemsAcc, { DayActivity }) => {
    const activities = DayActivity.reduce((activitiesAcc, { Activities }) => {
      return activitiesAcc.concat(Activities);
    }, [] as RegroupItemsStep1);

    return itemsAcc.concat(activities);
  }, [] as Activity[]);

  // Step 2 ➜ Regroup activities by local date.
  const step2: RegroupItemsStep2 = step1.reduce((newItemsAcc, activity) => {
    // event time using local timezone
    const localTime = moment.utc(activity.Time).local();
    // start of the day time using local timezone
    const startOfDay = moment(localTime).startOf('day');

    // last Item in the accumulator
    const lastItem = newItemsAcc.length > 0 && newItemsAcc[newItemsAcc.length - 1];
    // current day in the accumulator (last one)
    const currentDay = lastItem && moment(lastItem.Date);

    // take the chance to manipulate the activity (e.g. to use the local time)
    const parsedActivity = { ...activity, Time: localTime.format() };

    // If the day is different in local time, create a new date entry
    if (!currentDay || !startOfDay.isSame(currentDay)) {
      return [
        ...newItemsAcc,
        {
          Date: startOfDay.format(),
          DayActivity: {
            [activity.Initial]: [parsedActivity],
          },
        },
      ];
    }

    if (lastItem) {
      return [
        ...newItemsAcc.slice(0, newItemsAcc.length - 1),
        {
          ...lastItem,
          DayActivity: {
            ...lastItem.DayActivity,
            [activity.Initial]: [...(lastItem.DayActivity[activity.Initial] || []), parsedActivity],
          },
        },
      ];
    }

    return newItemsAcc;
  }, [] as RegroupItemsStep2);

  // Step 3 ➜ Convert to the expected structure (aka ActivityItem[])
  const step3: RegroupItemsStep3 = step2.map(({ Date, DayActivity }) => ({
    Date,
    DayActivity: Object.keys(DayActivity).map(Initial => ({
      Initial,
      Activities: DayActivity[Initial],
    })),
  }));

  return { ...data, Items: step3 };
};
